import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useApplicant } from "@/hooks/useApplicant";
import { useFormResponses } from "@/hooks/useFormResponses";
import { Field, Select, TextInput, YesNo } from "@/components/Field";
import { FieldHelpButton } from "@/components/FieldHelpButton";
import { cases } from "@/lib/endpoints";
import type { UpdatePersonRequest } from "@/lib/api-types";
import {
  combineFieldErrors,
  combineFormError,
  getFieldErrors,
  getFormError,
  runUpserts,
} from "@/lib/validation-errors";

/**
 * Step 1 — Personal information (Part 2 of N-400).
 *
 * Saves into the applicant person record. The form is locally controlled;
 * we PATCH only changed fields when the user clicks Save & continue.
 */

// Person-table fields — go to PATCH /persons/{pid}
interface PersonFormState {
  family_name: string;
  given_name: string;
  middle_name: string;
  date_of_birth: string;
  place_of_birth_city: string;
  place_of_birth_country: string;
  country_of_citizenship: string;
  country_of_nationality: string;
  current_immigration_status: string;
  gender: string;
  marital_status: string;
}

const emptyPerson: PersonFormState = {
  family_name: "",
  given_name: "",
  middle_name: "",
  date_of_birth: "",
  place_of_birth_city: "",
  place_of_birth_country: "",
  country_of_citizenship: "",
  country_of_nationality: "",
  current_immigration_status: "",
  gender: "",
  marital_status: "",
};

// form_responses fields — go to PUT /form-responses/.../{key}.
// Keys must match what the form mapping (n400_2025_01_20.go) reads.
const FR_KEYS = {
  // Identification (Item 5/6/12)
  aNumber: "a_number",
  uscisAccount: "uscis_online_account_number",
  ssn: "ssn",
  lprDate: "lpr_date",
  // Eligibility basis (Part 1)
  natBasis: "naturalization_basis",
  natOtherExplain: "naturalization_other_explanation",
  // Other names used (Part 2 Item 2) — up to 2 prior names
  otherName1Family: "other_name_1_family",
  otherName1Given: "other_name_1_given",
  otherName1Middle: "other_name_1_middle",
  otherName2Family: "other_name_2_family",
  otherName2Given: "other_name_2_given",
  otherName2Middle: "other_name_2_middle",
  // Name change (Part 2 Item 3-4)
  wantsNameChange: "wants_name_change",
  newLegalFamily: "new_legal_family_name",
  newLegalGiven: "new_legal_given_name",
  newLegalMiddle: "new_legal_middle_name",
  // Additional Part 2 questions (Items 10-11)
  parentUscBefore18: "parent_usc_before_18",
  needsDisabilityAccommodation: "needs_disability_accommodation",
} as const;

// Naturalization basis options. Values must match the strings the
// form-mapping rules engine recognises in n400_2025_01_20.go.
const NAT_BASIS_OPTIONS: { value: string; label: string }[] = [
  { value: "5_year", label: "5 years as a Lawful Permanent Resident (general)" },
  { value: "3_year", label: "3 years married to a U.S. citizen" },
  { value: "vawa", label: "VAWA self-petitioner (battered spouse/child)" },
  { value: "spouse_overseas", label: "Spouse of U.S. citizen working overseas (INA 319(b))" },
  { value: "military_one_year", label: "1 year of qualifying military service" },
  { value: "honorable_military", label: "Honorable military service during hostilities" },
  { value: "other", label: "Other (explain)" },
];


export function Step1Personal() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { applicant, isLoading, update, error } = useApplicant(id);
  const fr = useFormResponses(id);

  // Identity-defining fields lock once the case has produced a packet —
  // backend enforces (LockPersonIdentity / LockFormResponseIdentity
  // middleware) and returns 423 if anyone tries to PATCH them. Mirror
  // the lock in UI so users see the constraint before submitting. Same
  // ["case", id, "jobs"] cache key as CaseWorkspacePage / StepReview,
  // so the query is shared (no extra network round-trip in the common
  // case where the user navigated from the workspace).
  const jobsQ = useQuery({
    queryKey: ["case", id, "jobs"],
    queryFn: () => cases.listJobs(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
  const identityLocked = (jobsQ.data?.items ?? []).some((j) => j.state === "done");
  const lockHint = identityLocked
    ? "🔒 Locked after first packet generation — contact support to change."
    : undefined;

  // Field-level error map from the most recent PATCH /persons response.
  // Only populated when the server returns 422 validation_failed; for
  // other errors (network, 5xx) fieldErrors is null and the form-level
  // banner below carries the message.
  const fieldErrors = getFieldErrors(update.error);
  const formError = getFormError(update.error);

  // form_responses errors share a single mutation hook (one key at a
  // time, but they all run through fr.upsert). Multiple parallel
  // upserts race on fr.upsert.error — a later success clears the
  // earlier failure. We track all errors locally for the most recent
  // save attempt instead.
  const [saveErrors, setSaveErrors] = useState<unknown[]>([]);
  const frFieldErrors = combineFieldErrors(saveErrors);
  const frFormError = combineFormError(saveErrors);

  const [form, setForm] = useState<PersonFormState>(emptyPerson);
  const [aNumber, setANumber] = useState("");
  const [uscisAccount, setUscisAccount] = useState("");
  const [ssn, setSsn] = useState("");
  const [lprDate, setLprDate] = useState("");
  const [natBasis, setNatBasis] = useState("");
  const [natOtherExplain, setNatOtherExplain] = useState("");
  const [otherName1, setOtherName1] = useState({ family: "", given: "", middle: "" });
  const [otherName2, setOtherName2] = useState({ family: "", given: "", middle: "" });
  const [wantsNameChange, setWantsNameChange] = useState<boolean | null>(null);
  const [newLegal, setNewLegal] = useState({ family: "", given: "", middle: "" });
  const [parentUsc, setParentUsc] = useState<boolean | null>(null);
  const [needsDisability, setNeedsDisability] = useState<boolean | null>(null);

  // Hydrate person fields once the applicant loads.
  useEffect(() => {
    if (!applicant) return;
    setForm({
      family_name: applicant.family_name ?? "",
      given_name: applicant.given_name ?? "",
      middle_name: applicant.middle_name ?? "",
      date_of_birth: applicant.date_of_birth ?? "",
      place_of_birth_city: applicant.place_of_birth_city ?? "",
      place_of_birth_country: applicant.place_of_birth_country ?? "",
      country_of_citizenship: applicant.country_of_citizenship ?? "",
      country_of_nationality: applicant.country_of_nationality ?? "",
      current_immigration_status: applicant.current_immigration_status ?? "",
      gender: applicant.gender ?? "",
      marital_status: applicant.marital_status ?? "",
    });
  }, [applicant]);

  // Hydrate form_responses fields whenever the FR list changes.
  useEffect(() => {
    if (fr.isLoading) return;
    setANumber(fr.getString(FR_KEYS.aNumber));
    setUscisAccount(fr.getString(FR_KEYS.uscisAccount));
    setSsn(fr.getString(FR_KEYS.ssn));
    setLprDate(fr.getString(FR_KEYS.lprDate));
    setNatBasis(fr.getString(FR_KEYS.natBasis));
    setNatOtherExplain(fr.getString(FR_KEYS.natOtherExplain));
    setOtherName1({
      family: fr.getString(FR_KEYS.otherName1Family),
      given: fr.getString(FR_KEYS.otherName1Given),
      middle: fr.getString(FR_KEYS.otherName1Middle),
    });
    setOtherName2({
      family: fr.getString(FR_KEYS.otherName2Family),
      given: fr.getString(FR_KEYS.otherName2Given),
      middle: fr.getString(FR_KEYS.otherName2Middle),
    });
    const wnc = fr.get<boolean>(FR_KEYS.wantsNameChange);
    setWantsNameChange(typeof wnc === "boolean" ? wnc : null);
    setNewLegal({
      family: fr.getString(FR_KEYS.newLegalFamily),
      given: fr.getString(FR_KEYS.newLegalGiven),
      middle: fr.getString(FR_KEYS.newLegalMiddle),
    });
    const pu = fr.get<boolean>(FR_KEYS.parentUscBefore18);
    setParentUsc(typeof pu === "boolean" ? pu : null);
    const nd = fr.get<boolean>(FR_KEYS.needsDisabilityAccommodation);
    setNeedsDisability(typeof nd === "boolean" ? nd : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fr.isLoading, ...Object.values(FR_KEYS).map((k) => fr.get(k))]);

  const set = <K extends keyof PersonFormState>(key: K, value: PersonFormState[K]) =>
    setForm((s) => ({ ...s, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applicant) return;
    setSaveErrors([]);

    // 1) Person fields — PATCH only non-empty values.
    const patch: UpdatePersonRequest = {};
    (Object.keys(form) as (keyof PersonFormState)[]).forEach((k) => {
      if (form[k] !== "") (patch as Record<string, unknown>)[k] = form[k];
    });
    try {
      await update.mutateAsync(patch);
    } catch {
      // update.error is populated via react-query state — fieldErrors
      // and formError pick it up. Bail before triggering form_response
      // upserts; the user fixes the Person row first.
      return;
    }

    // 2) form_responses — upsert in parallel. Skip empty strings;
    //    persist booleans only once the user has actually answered
    //    (null = unanswered, no upsert).
    const upserts: Promise<unknown>[] = [];
    const upsertStr = (key: string, val: string) => {
      if (val) upserts.push(fr.upsert.mutateAsync({ fieldKey: key, value: val }));
    };
    const upsertBool = (key: string, val: boolean | null) => {
      if (val !== null) upserts.push(fr.upsert.mutateAsync({ fieldKey: key, value: val }));
    };
    upsertStr(FR_KEYS.aNumber, aNumber);
    upsertStr(FR_KEYS.uscisAccount, uscisAccount);
    upsertStr(FR_KEYS.ssn, ssn);
    upsertStr(FR_KEYS.lprDate, lprDate);
    upsertStr(FR_KEYS.natBasis, natBasis);
    if (natBasis === "other") {
      upsertStr(FR_KEYS.natOtherExplain, natOtherExplain);
    }
    upsertStr(FR_KEYS.otherName1Family, otherName1.family);
    upsertStr(FR_KEYS.otherName1Given, otherName1.given);
    upsertStr(FR_KEYS.otherName1Middle, otherName1.middle);
    upsertStr(FR_KEYS.otherName2Family, otherName2.family);
    upsertStr(FR_KEYS.otherName2Given, otherName2.given);
    upsertStr(FR_KEYS.otherName2Middle, otherName2.middle);
    upsertBool(FR_KEYS.wantsNameChange, wantsNameChange);
    if (wantsNameChange) {
      upsertStr(FR_KEYS.newLegalFamily, newLegal.family);
      upsertStr(FR_KEYS.newLegalGiven, newLegal.given);
      upsertStr(FR_KEYS.newLegalMiddle, newLegal.middle);
    }
    upsertBool(FR_KEYS.parentUscBefore18, parentUsc);
    upsertBool(FR_KEYS.needsDisabilityAccommodation, needsDisability);
    const errors = await runUpserts(upserts);
    if (errors.length > 0) {
      setSaveErrors(errors);
      return;
    }

    navigate(`/cases/${id}/wizard/biographic`);
  };

  if (isLoading) return <div className="text-slate-500">Loading…</div>;
  if (error) {
    return (
      <div className="text-red-600 text-sm">
        Could not load your application: {(error as Error).message}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Personal information</h1>
        <p className="text-sm text-slate-500 mt-1">
          Use your legal name exactly as it appears on your Permanent Resident
          Card. Other names you&apos;ve used go in the next step.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="Family (last) name"
          required
          hint={lockHint}
          error={fieldErrors?.["family_name"]?.message}
        >
          <TextInput
            value={form.family_name}
            onChange={(e) => set("family_name", e.target.value)}
            autoComplete="family-name"
            disabled={identityLocked}
          />
        </Field>
        <Field
          label="Given (first) name"
          required
          hint={lockHint}
          error={fieldErrors?.["given_name"]?.message}
        >
          <TextInput
            value={form.given_name}
            onChange={(e) => set("given_name", e.target.value)}
            autoComplete="given-name"
            disabled={identityLocked}
          />
        </Field>
        <Field
          label="Middle name"
          hint={lockHint ?? "Leave blank if you don't have one"}
          error={fieldErrors?.["middle_name"]?.message}
        >
          <TextInput
            value={form.middle_name}
            onChange={(e) => set("middle_name", e.target.value)}
            autoComplete="additional-name"
            disabled={identityLocked}
          />
        </Field>
        <Field
          label="Date of birth"
          required
          hint={lockHint}
          error={fieldErrors?.["date_of_birth"]?.message}
        >
          <TextInput
            type="date"
            value={form.date_of_birth}
            onChange={(e) => set("date_of_birth", e.target.value)}
            autoComplete="bday"
            disabled={identityLocked}
          />
        </Field>
        <Field
          label="City of birth"
          required
          hint={lockHint}
          error={fieldErrors?.["place_of_birth_city"]?.message}
        >
          <TextInput
            value={form.place_of_birth_city}
            onChange={(e) => set("place_of_birth_city", e.target.value)}
            disabled={identityLocked}
          />
        </Field>
        <Field
          label="Country of birth"
          required
          hint={lockHint}
          error={fieldErrors?.["place_of_birth_country"]?.message}
        >
          <TextInput
            value={form.place_of_birth_country}
            onChange={(e) => set("place_of_birth_country", e.target.value)}
            disabled={identityLocked}
          />
        </Field>
        <Field
          label="Country of citizenship"
          required
          error={fieldErrors?.["country_of_citizenship"]?.message}
        >
          <TextInput
            value={form.country_of_citizenship}
            onChange={(e) => set("country_of_citizenship", e.target.value)}
          />
        </Field>
        <Field
          label="Country of nationality"
          required
          hint="Usually the same as citizenship; differs only for stateless / dual-nationality applicants."
          error={fieldErrors?.["country_of_nationality"]?.message}
        >
          <TextInput
            value={form.country_of_nationality}
            onChange={(e) => set("country_of_nationality", e.target.value)}
          />
        </Field>
        <Field
          label="Current immigration status"
          required
          hint='e.g. "Lawful Permanent Resident", "Conditional Permanent Resident".'
          className="sm:col-span-2"
          error={fieldErrors?.["current_immigration_status"]?.message}
        >
          <TextInput
            value={form.current_immigration_status}
            onChange={(e) => set("current_immigration_status", e.target.value)}
          />
        </Field>
        <Field label="Gender" required error={fieldErrors?.["gender"]?.message}>
          <Select
            value={form.gender}
            onChange={(e) => set("gender", e.target.value)}
          >
            <option value="">Select…</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </Select>
        </Field>
        <Field
          label="Marital status"
          required
          className="sm:col-span-2"
          error={fieldErrors?.["marital_status"]?.message}
        >
          <Select
            value={form.marital_status}
            onChange={(e) => set("marital_status", e.target.value)}
          >
            <option value="">Select…</option>
            <option value="single">Single (never married)</option>
            <option value="married">Married</option>
            <option value="divorced">Divorced</option>
            <option value="widowed">Widowed</option>
            <option value="separated">Separated</option>
            <option value="annulled">Marriage annulled</option>
          </Select>
        </Field>
      </div>

      <fieldset className="border-t border-slate-200 pt-6 mt-2">
        <legend className="font-medium text-slate-900">Identification numbers</legend>
        <p className="text-sm text-slate-500 mt-1 mb-4">
          From your Permanent Resident Card and SSA records. Stored encrypted at rest.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label={<>A-Number (Alien Registration Number) <FieldHelpButton question="What is an A-Number (Alien Registration Number) and where can I find it on my Green Card?" fieldKey="a_number" /></>}
            required
            hint={lockHint ?? "On your Green Card. Format: A-XXX-XXX-XXX"}
            error={frFieldErrors?.["a_number"]?.message}
          >
            <TextInput
              value={aNumber}
              onChange={(e) => setANumber(e.target.value)}
              placeholder="A-123-456-789"
              disabled={identityLocked}
            />
          </Field>
          <Field
            label="USCIS Online Account Number"
            hint={lockHint ?? "If you've created a myUSCIS account. Optional."}
            error={frFieldErrors?.["uscis_online_account_number"]?.message}
          >
            <TextInput
              value={uscisAccount}
              onChange={(e) => setUscisAccount(e.target.value)}
              disabled={identityLocked}
            />
          </Field>
          <Field
            label={<>Social Security Number <FieldHelpButton question="Do I need to provide my Social Security Number on the N-400? What if I don't have one?" fieldKey="ssn" /></>}
            hint={lockHint ?? "Leave blank if you don't have one"}
            error={frFieldErrors?.["ssn"]?.message}
          >
            <TextInput
              value={ssn}
              onChange={(e) => setSsn(e.target.value)}
              placeholder="XXX-XX-XXXX"
              autoComplete="off"
              inputMode="numeric"
              disabled={identityLocked}
            />
          </Field>
          <Field
            label={<>Date you became a Permanent Resident <FieldHelpButton question="Which date do I enter for 'Date you became a Permanent Resident'? Is it the card issue date or my original approval date?" fieldKey="lpr_date" /></>}
            required
            hint="The date on your Green Card"
            error={frFieldErrors?.["lpr_date"]?.message}
          >
            <TextInput
              type="date"
              value={lprDate}
              onChange={(e) => setLprDate(e.target.value)}
            />
          </Field>
        </div>
      </fieldset>

      <fieldset className="border-t border-slate-200 pt-6 mt-2">
        <legend className="font-medium text-slate-900">Eligibility basis</legend>
        <p className="text-sm text-slate-500 mt-1 mb-4">
          Which rule lets you apply for citizenship now? Most applicants
          choose the 5-year general rule.
        </p>
        <div className="grid grid-cols-1 gap-4">
          <Field label={<>Naturalization basis <FieldHelpButton question="How do I know which naturalization basis applies to me?" fieldKey="naturalization_basis" /></>} required>
            <Select
              value={natBasis}
              onChange={(e) => setNatBasis(e.target.value)}
            >
              <option value="">Select…</option>
              {NAT_BASIS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          {natBasis === "other" && (
            <Field
              label="Explain your basis"
              hint="One or two sentences. USCIS will see this in Part 1 Item 5."
              required
              error={frFieldErrors?.[FR_KEYS.natOtherExplain]?.message}
            >
              <TextInput
                value={natOtherExplain}
                onChange={(e) => setNatOtherExplain(e.target.value)}
                placeholder="Filing under INA section …"
              />
            </Field>
          )}
        </div>
      </fieldset>

      <fieldset className="border-t border-slate-200 pt-6 mt-2">
        <legend className="font-medium text-slate-900">Other names you've used</legend>
        <p className="text-sm text-slate-500 mt-1 mb-4">
          Maiden names, nicknames, names from previous marriages — anything
          you've used legally or unofficially. Skip if there are none.
        </p>
        <div className="space-y-4">
          {[
            {
              label: "Other name 1",
              state: otherName1,
              set: setOtherName1,
              keys: {
                family: FR_KEYS.otherName1Family,
                given: FR_KEYS.otherName1Given,
                middle: FR_KEYS.otherName1Middle,
              },
            },
            {
              label: "Other name 2",
              state: otherName2,
              set: setOtherName2,
              keys: {
                family: FR_KEYS.otherName2Family,
                given: FR_KEYS.otherName2Given,
                middle: FR_KEYS.otherName2Middle,
              },
            },
          ].map((row) => (
            <div key={row.label}>
              <p className="text-sm font-medium text-slate-700 mb-2">{row.label}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field
                  label="Family name"
                  error={frFieldErrors?.[row.keys.family]?.message}
                >
                  <TextInput
                    value={row.state.family}
                    onChange={(e) => row.set({ ...row.state, family: e.target.value })}
                  />
                </Field>
                <Field
                  label="Given name"
                  error={frFieldErrors?.[row.keys.given]?.message}
                >
                  <TextInput
                    value={row.state.given}
                    onChange={(e) => row.set({ ...row.state, given: e.target.value })}
                  />
                </Field>
                <Field
                  label="Middle name"
                  error={frFieldErrors?.[row.keys.middle]?.message}
                >
                  <TextInput
                    value={row.state.middle}
                    onChange={(e) => row.set({ ...row.state, middle: e.target.value })}
                  />
                </Field>
              </div>
            </div>
          ))}
        </div>
      </fieldset>

      <fieldset className="border-t border-slate-200 pt-6 mt-2">
        <legend className="font-medium text-slate-900">Legal name change</legend>
        <p className="text-sm text-slate-500 mt-1 mb-4">
          You can ask USCIS to legally change your name as part of
          naturalization — courts honour the new name on your certificate.
        </p>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">
              Would you like to legally change your name?
            </p>
            <YesNo value={wantsNameChange} onChange={setWantsNameChange} />
          </div>
          {wantsNameChange === true && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field
                label="New family name"
                required
                error={frFieldErrors?.[FR_KEYS.newLegalFamily]?.message}
              >
                <TextInput
                  value={newLegal.family}
                  onChange={(e) => setNewLegal({ ...newLegal, family: e.target.value })}
                />
              </Field>
              <Field
                label="New given name"
                required
                error={frFieldErrors?.[FR_KEYS.newLegalGiven]?.message}
              >
                <TextInput
                  value={newLegal.given}
                  onChange={(e) => setNewLegal({ ...newLegal, given: e.target.value })}
                />
              </Field>
              <Field
                label="New middle name"
                error={frFieldErrors?.[FR_KEYS.newLegalMiddle]?.message}
              >
                <TextInput
                  value={newLegal.middle}
                  onChange={(e) => setNewLegal({ ...newLegal, middle: e.target.value })}
                />
              </Field>
            </div>
          )}
        </div>
      </fieldset>

      <fieldset className="border-t border-slate-200 pt-6 mt-2">
        <legend className="font-medium text-slate-900">Additional questions</legend>
        <div className="space-y-5 mt-4">
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">
              Did either of your parents become a U.S. citizen before
              your 18th birthday?
            </p>
            <p className="text-xs text-slate-500 mb-2">
              If yes, you may already be a U.S. citizen by derivation —
              talk to a lawyer before continuing this application.
            </p>
            <YesNo value={parentUsc} onChange={setParentUsc} />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">
              Do you need a disability accommodation for the naturalization
              interview?
            </p>
            <p className="text-xs text-slate-500 mb-2">
              For example: a sign-language interpreter, wheelchair access,
              or a waiver of the English/civics tests for medical reasons.
            </p>
            <YesNo value={needsDisability} onChange={setNeedsDisability} />
          </div>
        </div>
      </fieldset>

      {/* Form-level banner: only when error isn't a field-level 422
          (those light up individual inputs above). Network / 5xx / 423
          identity-lock errors land here. */}
      {formError && (
        <p className="text-sm text-red-600">Could not save: {formError}</p>
      )}
      {frFormError && (
        <p className="text-sm text-red-600">Could not save: {frFormError}</p>
      )}
      {(fieldErrors || frFieldErrors) && (
        <p className="text-xs text-red-600">
          Some fields above need attention before continuing.
        </p>
      )}

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
        <button
          type="submit"
          /* Disable across the whole save sequence — update.mutateAsync
             completes BEFORE the fr.upsert batch finishes, so gating on
             update.isPending alone leaves a re-clickable window. */
          disabled={update.isPending || fr.upsert.isPending}
          className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {update.isPending || fr.upsert.isPending ? "Saving…" : "Save & continue"}
        </button>
      </div>
    </form>
  );
}
